import React, {
  useContext, useEffect, useReducer, useRef
} from 'react';
import PropTypes from 'prop-types';
import { Select } from '@glu/form-components';
import { withStyles, withTheme } from '@glu/theming';
import locale from '@glu/locale';
import SchedulerContext from '../SchedulerContext';
import {
  MinutesHours, Daily, Weekly, Monthly, SemiMonthly, Yearly
} from './index';
import {
  FRI,
  getDefaultDayInMonth,
  getDefaultDayInYear,
  getRecurrenceOptions, MON, SAT,
  setupRecurrence, SUN, THU, TUE, WED
} from '../utils/recurrence';
import reducer, {
  types
} from './Recurrence.reducer';
import { getHourExamplesForDay, getMinutesExamplesForDay } from '../utils/date';
import { generateTime } from '../utils/time';

const styles = ({ typography, palette }) => ({
  recurrence: {},
  occurrence: {
    fontFamily: typography.fontFamily
  },
  error: {
    color: palette.form.error
  }
});

const Recurrence = ({
  classes, recurrenceType, setRecurrenceType, getValueOnStart, onChange, recurrenceError,
  minutes, hourly, daily, weekly, monthly, semiMonthly, yearly,
  minTime, maxTime, minuteStep, exactMinuteStep, hideLastDayInDays, ...rest
}) => {
  const {
    dataQa, htmlId, disabled, timePicker24Hour
  } = useContext(SchedulerContext);
  const {
    nMinutes, nHours, nDay, nWeeks, nMonths, nSemiMonths, nYears, intervalType, onN = [], atTime
  } = rest;
  const recurrenceOptions = getRecurrenceOptions({
    minutes, hourly, daily, weekly, monthly, semiMonthly, yearly
  });
  const isObjectDays = onN && typeof onN[0] === 'object';
  const defaultDayObject = (day, canSetTime) => (
    getDefaultDayInMonth(canSetTime, timePicker24Hour, {
      day,
      atTime: atTime || minTime || generateTime()
    })
  );
  const [recurrence, dispatch] = useReducer(reducer, {
    nMinutes: nMinutes || minuteStep || 1,
    nHours: nHours || 1,
    nDay: nDay || 1,
    nWeeks: nWeeks || 1,
    nMonths: nMonths || 1,
    nSemiMonths: nSemiMonths || 1,
    nYears,
    intervalType,
    onN,
    atTime: atTime || minTime || generateTime(),
    daysInWeek: recurrenceType === types.WEEKLY && !isObjectDays && onN.length > 0
      ? onN
      : undefined,
    daysInSemiMonth: onN && isObjectDays
      ? setupRecurrence(onN)
      : [
        defaultDayObject(1, semiMonthly && semiMonthly.canSetTime),
        defaultDayObject(2, semiMonthly && semiMonthly.canSetTime)
      ],
    daysInMonth: onN && isObjectDays
      ? setupRecurrence(onN)
      : [defaultDayObject(1, monthly && monthly.canSetTime)],
    daysInYear: recurrenceType === types.YEARLY && onN && isObjectDays
      ? setupRecurrence(onN)
      : [getDefaultDayInYear(yearly && yearly.canSetTime, timePicker24Hour)]
  });

  const prevRecurrence = useRef();

  useEffect(() => {
    // Avoid onChange on first render
    if (prevRecurrence.current || getValueOnStart) onChange(recurrence);
    prevRecurrence.current = recurrence;
  }, [getValueOnStart, onChange, recurrence]);

  const renderRecurrenceType = () => {
    switch (recurrenceType) {
      case types.MINUTES:
        return (
          <MinutesHours
            {...recurrence}
            everyText={locale.get('schedulerReact.everyXMinutes').split('{0}')}
            exactStep={exactMinuteStep}
            step={minuteStep}
            min={1}
            max={59}
            value={recurrence.nMinutes}
            type={types.MINUTES}
            getExampleText={getMinutesExamplesForDay}
            onChange={dispatch}
          />
        );
      case types.HOURLY:
        return (
          <MinutesHours
            {...recurrence}
            everyText={locale.get('schedulerReact.everyXHours').split('{0}')}
            min={1}
            max={23}
            value={recurrence.nHours}
            type={types.HOURLY}
            getExampleText={getHourExamplesForDay}
            onChange={dispatch}
          />
        );
      case types.DAILY:
        return (
          <Daily
            {...daily}
            {...recurrence}
            onChange={dispatch}
            minTime={minTime}
            maxTime={maxTime}
          />
        );
      case types.WEEKLY:
        return (
          <Weekly
            {...weekly}
            {...recurrence}
            onChange={dispatch}
            minTime={minTime}
            maxTime={maxTime}
          />
        );
      case types.MONTHLY:
        return (
          <Monthly
            {...monthly}
            {...recurrence}
            hideLastDayInDays={hideLastDayInDays}
            onChange={dispatch}
            minTime={minTime}
            maxTime={maxTime}
          />
        );
      case types.SEMIMONTHLY:
        return (
          <SemiMonthly
            {...semiMonthly}
            {...recurrence}
            hideLastDayInDays={hideLastDayInDays}
            onChange={dispatch}
            minTime={minTime}
            maxTime={maxTime}
          />
        );
      case types.YEARLY:
        return (
          <Yearly
            {...yearly}
            {...recurrence}
            onChange={dispatch}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div data-qa={`occurrence-${dataQa || htmlId}`} className={`${classes.occurrence} `}>
      <Select
        htmlId={`${htmlId}-typeSelect`}
        name={`${htmlId}-typeSelect`}
        data-qa="recurrence-type-select"
        disabled={disabled}
        options={recurrenceOptions}
        labelText={locale.get('schedulerReact.repeat')}
        value={recurrenceType}
        disableInputAPI
        onChange={(e) => setRecurrenceType(e.target.value)}
      />
      {renderRecurrenceType()}
      {recurrenceError && <div className={classes.error}>{recurrenceError}</div>}
    </div>
  );
};

Recurrence.propTypes = {
  /** Classes provided by the theme */
  classes: PropTypes.objectOf(PropTypes.string).isRequired,

  /** Get scheduler state on initial render */
  getValueOnStart: PropTypes.bool,

  /** Type of recurrence */
  recurrenceType: PropTypes.oneOf([
    types.MINUTES, types.HOURLY, types.DAILY, types.WEEKLY,
    types.MONTHLY, types.SEMIMONTHLY, types.YEARLY
  ]),

  /** Display recurrence by minute */
  minutes: PropTypes.bool.isRequired,

  /** Hide 'last' day in days dropdown */
  hideLastDayInDays: PropTypes.bool,

  /** Display recurrence by hour */
  hourly: PropTypes.bool.isRequired,

  /** Display recurrence by day */
  daily: PropTypes.shape({
    enabled: PropTypes.bool,
    canSetTime: PropTypes.bool
  }).isRequired,

  /** Display recurrence by week */
  weekly: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      enabled: PropTypes.bool,
      canSetTime: PropTypes.bool,
      allowWeekend: PropTypes.bool,
      defaultValue: PropTypes.arrayOf(PropTypes.oneOf([SUN, MON, TUE, WED, THU, FRI, SAT]))
    })
  ]).isRequired,

  /** Display recurrence by month */
  monthly: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      enabled: PropTypes.bool,
      canSetTime: PropTypes.bool
    })
  ]).isRequired,

  /** Display recurrence by semi monthly */
  semiMonthly: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      enabled: PropTypes.bool,
      canSetTime: PropTypes.bool
    })
  ]).isRequired,

  /** Display recurrence by year */
  yearly: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      enabled: PropTypes.bool,
      canSetTime: PropTypes.bool
    })
  ]).isRequired,

  /** Minimum time value of input */
  minTime: PropTypes.string,

  /** Maximum time value of input */
  maxTime: PropTypes.string,

  /** Value for increment minutes with */
  minuteStep: PropTypes.number,

  /** Avoid to enter minutes manually. The user can change the minutes only by +- buttons */
  exactMinuteStep: PropTypes.bool,

  /** Callback to change recurrence type */
  setRecurrenceType: PropTypes.func.isRequired,

  /** Error on recurrence */
  recurrenceError: PropTypes.string,

  /** onChange of recurrence object */
  onChange: PropTypes.func.isRequired
};

Recurrence.defaultProps = {
  exactMinuteStep: false,
  getValueOnStart: false,
  hideLastDayInDays: false,
  minTime: undefined,
  maxTime: undefined,
  minuteStep: undefined,
  recurrenceType: null,
  recurrenceError: undefined
};

export const BareRecurrence = Recurrence;
export default withTheme(withStyles(styles)(Recurrence));
